// Schema-driven definitions for every admin-editable content area.
// `fieldSchema` edits an object payload; `listSchema` edits a list payload;
// `lists` adds list sections inside an object payload.

export const AREAS = [
  {
    id: "home",
    label: "Home page",
    key: "home",
    preview: "/",
    help: "Hero text, section visibility, button destinations, and the closing message.",
    fieldSchema: [
      { name: "heroEyebrow", label: "Hero eyebrow line" },
      { name: "heroLine1", label: "Hero headline — line 1" },
      { name: "heroLine2", label: "Hero headline — line 2" },
      { name: "heroLine3", label: "Hero headline — line 3 (highlighted)" },
      { name: "heroSubtext", label: "Hero supporting copy", type: "textarea" },
      { name: "rememberingText", label: "'Remembering TJ' text", type: "textarea", rows: 4 },
      { name: "missionText", label: "Mission text", type: "textarea", rows: 4 },
      { name: "walkText", label: "Walking for Hope text", type: "textarea" },
      { name: "closingLine1", label: "Closing message — line 1" },
      { name: "closingLine2", label: "Closing message — line 2 (highlighted)" },
      { name: "showChoose", label: "Show 'Choose what you need' section", type: "toggle" },
      { name: "showRemembering", label: "Show 'Remembering TJ' section", type: "toggle" },
      { name: "showMission", label: "Show mission section", type: "toggle" },
      { name: "showWalk", label: "Show Walking for Hope section", type: "toggle" },
      { name: "showInvolved", label: "Show 'Get involved' section", type: "toggle" },
      { name: "showClosing", label: "Show closing support message", type: "toggle" },
    ],
    lists: [
      {
        name: "heroButtons",
        label: "Hero buttons",
        addLabel: "Add button",
        itemTitle: (b) => b.label || "Untitled button",
        itemSchema: [
          { name: "label", label: "Button label" },
          { name: "to", label: "Destination (e.g. /support)", placeholder: false },
        ],
      },
    ],
  },
  {
    id: "about",
    label: "About TJ",
    key: "about",
    preview: "/about",
    help: "TJ's story, mission wording, quotes, and the faith note. The mother's story publishes only when you write it here.",
    fieldSchema: [
      { name: "intro", label: "Page introduction", type: "textarea", rows: 3 },
      { name: "whyText", label: "'Why TJ's Light exists' text", type: "textarea", rows: 4 },
      { name: "missionText", label: "Mission text", type: "textarea", rows: 3 },
      { name: "quote", label: "Featured family message", type: "textarea", rows: 3 },
      { name: "quoteAttribution", label: "Quote attribution" },
      { name: "faithVerse", label: "Faith verse" },
      { name: "faithRef", label: "Verse reference" },
      { name: "faithNote", label: "Faith note (never a substitute for support)", type: "textarea", rows: 3 },
      { name: "keepText", label: "'Keep TJ's light shining' text", type: "textarea", rows: 3 },
      { name: "storyShort", label: "A Mother's Story — short version", type: "textarea", rows: 5, hint: "Empty = placeholder shown until the family is ready." },
    ],
    lists: [
      {
        name: "storyFull",
        label: "A Mother's Story — full sections",
        addLabel: "Add story section",
        itemTitle: (s) => s.heading || "Untitled section",
        itemSchema: [
          { name: "heading", label: "Section heading" },
          { name: "body", label: "Section text", type: "textarea", rows: 5 },
        ],
      },
    ],
  },
  {
    id: "gallery",
    label: "Photos",
    key: "gallery",
    preview: "/photos",
    help: "Gallery images with captions, alt text, and display order. An image cannot be published without alt text.",
    listSchema: {
      addLabel: "Add photo",
      itemTitle: (g) => g.caption || g.alt || "Untitled photo",
      itemSchema: [
        { name: "src", label: "Photo (upload or paste an image link)", type: "url", image: true, placeholder: false },
        { name: "alt", label: "Image description (alt text) — required with an image", type: "textarea", rows: 2 },
        { name: "caption", label: "Caption" },
      ],
    },
  },
  {
    id: "event",
    label: "Walk — event details",
    key: "event",
    preview: "/walking-for-hope",
    help: "Walking for Hope date, time, location, route, check-in, schedule, parking, and accessibility. Empty fields show 'Details coming soon'.",
    fieldSchema: [
      { name: "date", label: "Date" },
      { name: "startTime", label: "Start time" },
      { name: "location", label: "Location" },
      { name: "checkInTime", label: "Check-in details" },
      { name: "route", label: "Route (short summary)", type: "textarea", rows: 2 },
      { name: "schedule", label: "Full schedule", type: "textarea", rows: 3 },
      { name: "routeSteps", label: "Turn-by-turn route steps — one per line", type: "textarea", rows: 5 },
      { name: "routeNote", label: "Route note (e.g. schematic disclaimer)", type: "textarea", rows: 2 },
      { name: "routeMapUrl", label: "Route map image", type: "url", image: true, placeholder: false },
      { name: "routeMapAlt", label: "Route map description (alt text)", type: "textarea", rows: 3 },
      { name: "registrationDeadline", label: "Registration deadline" },
      { name: "parkingAccessibility", label: "Parking and accessibility", type: "textarea", rows: 3 },
      { name: "whatToBring", label: "What to bring — one item per line", type: "textarea", rows: 5 },
      { name: "bringNote", label: "'Walk at your own pace' note", type: "textarea", rows: 2 },
      {name: "eventContact", label: "Event contact and announcements", type: "textarea", rows: 2 },
      { name: "merchIntro", label: "Merchandise intro", type: "textarea", rows: 2 },
      { name: "shirtPrice", label: "Shirt price (e.g. $15 each)" },
      { name: "buttonPrice", label: "Button price (e.g. $5 each)" },
      { name: "buttonNote", label: "Button note", type: "textarea", rows: 2 },
      { name: "orderSteps", label: "How to order — one step per line", type: "textarea", rows: 5 },
      { name: "paymentNote", label: "Payment note (merchandise Zelle only)", type: "textarea", rows: 3 },
      { name: "flyerUrl", label: "Event flyer image", type: "url", image: true, placeholder: false },
      { name: "flyerAlt", label: "Flyer description (alt text)", type: "textarea", rows: 3 },
      { name: "zelleQrUrl", label: "Merchandise payment QR image", type: "url", image: true, placeholder: false },
      { name: "zelleQrAlt", label: "Payment QR description (alt text)" },
      { name: "cashAppUrl", label: "Cash App payment link (tap-to-pay)", type: "url", placeholder: false },
      { name: "paperNote", label: "'Prefer paper?' note above the form", type: "textarea", rows: 3 },
      { name: "regFormUrl", label: "Paper registration form (image)", type: "url", image: true, placeholder: false },
      { name: "regFormPdfUrl", label: "Paper registration form (print PDF)", type: "url", placeholder: false },
      { name: "regFormAlt", label: "Registration form description (alt text)", type: "textarea", rows: 3 },
    ],
  },
  {
    id: "registration",
    label: "Walk — registration",
    key: "registration",
    preview: "/walking-for-hope",
    help: "Open or close event registration and choose which optional fields the form collects. Only the minimum needed is collected.",
    fieldSchema: [
      { name: "open", label: "Registration is open", type: "toggle", hint: "When off, the form is hidden and no entries can be submitted." },
      { name: "note", label: "Note shown while registration is closed", type: "textarea", rows: 2 },
      { name: "showTeamOption", label: "Offer individual/team choice", type: "toggle" },
      { name: "showAccessibility", label: "Collect accessibility requests", type: "toggle" },
      { name: "showPhotoPermission", label: "Ask photo permission (unchecked by default)", type: "toggle" },
    ],
  },
  {
    id: "faqs",
    label: "Walk — FAQs",
    key: "faqs",
    preview: "/walking-for-hope",
    help: "Event questions and answers.",
    listSchema: {
      addLabel: "Add question",
      itemTitle: (f) => f.q || "Untitled question",
      itemSchema: [
        { name: "q", label: "Question" },
        { name: "a", label: "Answer", type: "textarea", rows: 3 },
      ],
    },
  },
  {
    id: "news",
    label: "News & Updates",
    key: "news",
    preview: "/news",
    help: "Create, edit, publish, unpublish, and delete posts. Only posts marked published appear on the site. The sample announcement disappears once you publish your own list.",
    listSchema: {
      addLabel: "Add post",
      itemTitle: (n) => n.title || "Untitled post",
      itemSchema: [
        { name: "title", label: "Title" },
        { name: "date", label: "Date (e.g. October 4, 2026)" },
        { name: "body", label: "Post text", type: "textarea", rows: 4 },
        { name: "published", label: "Published", type: "toggle", placeholder: false },
      ],
    },
  },
  {
    id: "resources",
    label: "Support resources",
    key: "resources",
    preview: "/support",
    help: "Crisis and mental-health resource links, grouped by topic, each with a last-reviewed date.",
    listSchema: {
      addLabel: "Add resource",
      itemTitle: (r) => r.name || "Untitled resource",
      itemSchema: [
        { name: "topic", label: "Topic / category (groups entries together)" },
        { name: "name", label: "Provider name" },
        { name: "description", label: "Short description", type: "textarea", rows: 3 },
        { name: "href", label: "Link URL", type: "url", placeholder: false },
        { name: "action", label: "Link label (e.g. 988lifeline.org)" },
        { name: "reviewed", label: "Last reviewed (e.g. July 2026)" },
      ],
    },
  },
  {
    id: "volunteer",
    label: "Volunteer",
    key: "volunteer",
    preview: "/get-involved#volunteer",
    help: "Volunteer section wording and whether the interest form accepts submissions.",
    fieldSchema: [
      { name: "intro", label: "Intro text", type: "textarea", rows: 3 },
      { name: "enabled", label: "Volunteer form accepts submissions", type: "toggle" },
    ],
  },
  {
    id: "sponsor",
    label: "Sponsor & partner",
    key: "sponsor",
    preview: "/get-involved#sponsor",
    help: "Sponsor/partner wording, package note, and whether the inquiry form accepts submissions.",
    fieldSchema: [
      { name: "intro", label: "Intro text", type: "textarea", rows: 3 },
      { name: "packagesNote", label: "Sponsorship packages note", type: "textarea", rows: 2 },
      { name: "enabled", label: "Sponsor inquiry form accepts submissions", type: "toggle" },
    ],
  },
  {
    id: "donations",
    label: "Donations",
    key: "donations",
    preview: "/get-involved#donate",
    help: "Donation wording and payment-provider setting. No card details are ever stored. Tax-deductible wording is blocked until verified status is confirmed.",
    fieldSchema: [
      { name: "heading", label: "Section heading" },
      { name: "intro", label: "Intro text", type: "textarea", rows: 3 },
      { name: "amountsText", label: "Suggested amounts (comma-separated)", hint: "e.g. 10, 25, 50, 100" },
      { name: "provider", label: "Payment provider (empty = not connected)", hint: "Set only once a real provider account exists, e.g. stripe." },
      { name: "setupNote", label: "'Not connected yet' note", type: "textarea", rows: 3 },
      { name: "taxNote", label: "Tax wording (only with verified status)", type: "textarea", rows: 2 },
      { name: "taxApproved", label: "Tax wording approved — status verified", type: "toggle" },
    ],
  },
  {
    id: "contact",
    label: "Contact",
    key: "contact",
    preview: "/contact",
    help: "Contact email, social links, a short note, and whether the contact form accepts messages. The crisis notice beside the form cannot be removed.",
    fieldSchema: [
      { name: "email", label: "Foundation email (empty = 'coming soon')" },
      { name: "note", label: "Extra note on the contact page", type: "textarea", rows: 2 },
      { name: "formEnabled", label: "Contact form accepts messages", type: "toggle" },
    ],
    lists: [
      {
        name: "socials",
        label: "Social media links",
        addLabel: "Add social link",
        itemTitle: (s) => s.name || "Untitled link",
        itemSchema: [
          { name: "name", label: "Platform (e.g. Facebook)" },
          { name: "href", label: "Profile URL", type: "url", placeholder: false },
        ],
      },
    ],
  },
  {
    id: "site",
    label: "Site & footer",
    key: "site",
    preview: "/",
    help: "Organization name, motto, global notice banner, and footer disclaimer.",
    fieldSchema: [
      { name: "name", label: "Organization name" },
      { name: "shortName", label: "Short name" },
      { name: "motto", label: "Motto" },
      { name: "tagline", label: "Tagline", type: "textarea", rows: 2 },
      { name: "notice", label: "Global site notice (empty = hidden)", type: "textarea", rows: 2, placeholder: false },
      { name: "footerDisclaimer", label: "Footer crisis disclaimer", type: "textarea", rows: 2 },
    ],
  },
  {
    id: "crisis",
    label: "Crisis links",
    key: "crisis",
    preview: "/support",
    help: "These power 'Get Help Now' and the crisis actions on every page. They cannot be removed, and chat must point to the official 988lifeline.org.",
    danger: true,
    fieldSchema: [
      { name: "callHref", label: "Call link (must start with tel:)", placeholder: false },
      { name: "textHref", label: "Text link (must start with sms:)", placeholder: false },
      { name: "chatHref", label: "Chat link (official 988lifeline.org)", type: "url", placeholder: false },
      { name: "lifelineHref", label: "Lifeline home URL", type: "url", placeholder: false },
      { name: "helpSomeoneHref", label: "'Help someone' guidance URL", type: "url", placeholder: false },
    ],
  },
  {
    id: "images",
    label: "Brand & images",
    key: "images",
    preview: "/",
    help: "The lighthouse logo, the hero photograph of TJ and his mother, and supporting imagery. Replace the URLs anytime — alt text is required before an image can be published.",
    fieldSchema: [
      { name: "logoUrl", label: "Logo image", type: "url", image: true, placeholder: false },
      { name: "logoAlt", label: "Logo alt text", type: "textarea", rows: 2 },
      { name: "heroBannerUrl", label: "Hero banner (Home page top)", type: "url", image: true, placeholder: false },
      { name: "heroBannerAlt", label: "Hero banner description (alt text)", type: "textarea", rows: 3 },
      { name: "tjMomUrl", label: "TJ and his mother photo", type: "url", image: true, placeholder: false },
      { name: "tjMomAlt", label: "TJ and his mother — description (alt text)", type: "textarea", rows: 3 },
      { name: "tjPortraitUrl", label: "TJ portrait (About TJ page)", type: "url", image: true, placeholder: false },
      { name: "tjPortraitAlt", label: "TJ portrait description (alt text)", type: "textarea", rows: 2 },
      { name: "walkLogoUrl", label: "Walking for Hope logo", type: "url", image: true, placeholder: false },
      { name: "walkLogoAlt", label: "Walking for Hope logo description (alt text)", type: "textarea", rows: 2 },
      { name: "heroLandscape", label: "Supporting image — landscape", type: "url", image: true, placeholder: false },
      { name: "walkVenueUrl", label: "Walk venue photo (Home page walk section)", type: "url", image: true, placeholder: false },
      { name: "walkVenueAlt", label: "Venue photo description (alt text)", type: "textarea", rows: 2 },
      { name: "holdingHands", label: "Supporting image — holding hands", type: "url", image: true, placeholder: false },
      { name: "unityHands", label: "Supporting image — unity hands", type: "url", image: true, placeholder: false },
    ],
  },
  {
    id: "seo",
    label: "SEO titles",
    key: "seo",
    preview: "/",
    help: "Page titles and descriptions shown in search results and when links are shared.",
    listSchema: {
      addLabel: "Add page",
      itemTitle: (s) => s.path || "Untitled page",
      itemSchema: [
        { name: "path", label: "Page path (e.g. /about)" },
        { name: "title", label: "Title (50–60 characters)", type: "textarea", rows: 2 },
        { name: "description", label: "Description (150–160 characters)", type: "textarea", rows: 3 },
      ],
    },
  },
];
